import React, { useState, useEffect, useRef } from "react";
import { useModalProtection } from "@/hooks/useModalProtection";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useSecureInsert } from "@/hooks/useSecureInsert";
import { useContratsST } from "@/hooks/useContratsST";
import { useSousTraitants } from "@/hooks/useSousTraitants";
import { Plus, Trash2, Loader2, Edit } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ClaudeVisionImport from "@/components/ocr/ClaudeVisionImport";
import ConfirmDialog from "@/components/ConfirmDialog";
import { cn } from "@/lib/utils";
import { SousTraitantSection, ContratsSTRecap } from "@/components/project/expenses";
import { getSpecialiteLabel } from "@/types/sous-traitant";
import type { TypeFactureST } from "@/types/sous-traitant";

// Schema de validation Zod pour les frais
const fraisSchema = z.object({
  type_frais: z.string().min(1, "Le type de coût est obligatoire"),
  montant_ht: z.number().nonnegative("Le montant ne peut pas être négatif"),
  description: z.string().optional(),
  date_frais: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Format de date invalide"),
});

interface ExpensesManagerProps {
  chantierId: string;
  frais: any[];
  onUpdate: () => void;
  isHighlighted?: boolean;
  directSaveMode?: boolean;  // Si true, OCR enregistre directement en BDD
}

const ExpensesManager = ({ chantierId, frais, onUpdate, isHighlighted = false, directSaveMode = false }: ExpensesManagerProps) => {
  const { toast } = useToast();
  const { secureInsert } = useSecureInsert();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type_frais: "",
    montant_ht: 0, // Utiliser HT pour les calculs de rentabilité
    tva_pct: 20, // TVA% par défaut (20% standard BTP)
    description: "",
    date_frais: new Date().toISOString().split('T')[0],
  });
  // État pour arrêter le clignotement dès que l'utilisateur clique sur le select type
  const [typeClicked, setTypeClicked] = useState(false);
  // Protection fermeture quand l'OCR a des données (multi-scan ou mono)
  const ocrHasDataRef = useRef(false);

  // État pour l'édition
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeletingFrais, setIsDeletingFrais] = useState(false);

  // Protection contre les refetch Realtime quand un dialog de saisie est ouvert
  useModalProtection(open);
  useModalProtection(editDialogOpen);

  // État pour l'édition des frais Sous-traitant
  const [editSelectedSTId, setEditSelectedSTId] = useState<string | null>(null);
  const [editSelectedContratId, setEditSelectedContratId] = useState<string | null>(null);
  const [editTypeFacture, setEditTypeFacture] = useState<TypeFactureST>('facture');

  // État pour la gestion des sous-traitants
  const [entrepriseId, setEntrepriseId] = useState<string | null>(null);
  const [selectedSTId, setSelectedSTId] = useState<string | null>(null);
  const [selectedContratId, setSelectedContratId] = useState<string | null>(null);
  const [typeFacture, setTypeFacture] = useState<TypeFactureST>('facture');

  // Hook pour les contrats ST du chantier (avec factures liées)
  const { contrats: contratsSTChantier, facturesParContrat, deleteContratST, refetch: refetchContratsST, isLoading: loadingContratsST } = useContratsST(chantierId);

  // Hook pour les sous-traitants
  const { sousTraitants } = useSousTraitants(entrepriseId);

  // Récupérer l'entreprise_id au montage
  useEffect(() => {
    const fetchEntrepriseId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: entreprise } = await supabase
        .from('entreprises')
        .select('id')
        .eq('proprietaire_user_id', user.id)
        .single();

      if (entreprise) {
        setEntrepriseId(entreprise.id);
      }
    };
    fetchEntrepriseId();
  }, []);

  // Reset les sélections ST quand le modal se ferme ou quand le type change
  useEffect(() => {
    if (!open || formData.type_frais !== 'Sous-traitant') {
      setSelectedSTId(null);
      setSelectedContratId(null);
      setTypeFacture('facture');
    }
  }, [open, formData.type_frais]);

  // Highlight type de coût tant qu'il n'est pas sélectionné ET que l'utilisateur n'a pas encore cliqué
  const isTypeSelected = formData.type_frais !== '';
  const shouldHighlightType = isHighlighted && open && !isTypeSelected && !typeClicked;

  // Ouvrir le modal avec un contrat pré-sélectionné (appelé depuis ContratsSTRecap)
  const handleAddFactureFromContrat = (contratId: string, sousTraitantId: string) => {
    // Pré-remplir le formulaire
    setFormData({
      type_frais: 'Sous-traitant',
      montant_ht: 0,
      tva_pct: 20,
      description: '',
      date_frais: new Date().toISOString().split('T')[0],
    });
    setSelectedSTId(sousTraitantId);
    setSelectedContratId(contratId);
    setTypeFacture('facture');
    // Ouvrir le modal
    setOpen(true);
  };

  // Types de frais - "Sous-traitant" en 2ème position
  const expenseCategories = [
    { value: "Location de véhicule", label: "Location de véhicule", icon: "🚗" },
    { value: "Sous-traitant", label: "Sous-traitant", icon: "👷" },  // Position 2
    { value: "Achat d'outillage", label: "Achat d'outillage", icon: "🔧" },
    { value: "Location d'outillage", label: "Location d'outillage", icon: "🔨" },
    { value: "Consommables", label: "Consommables", icon: "🍞" },
    { value: "Frais de déplacement", label: "Frais de déplacement", icon: "🚙" },
    { value: "Frais d'hébergement", label: "Frais d'hébergement", icon: "🏠" },
    { value: "Assurance", label: "Assurance", icon: "🛡️" },
    { value: "Permis & autorisations", label: "Permis & autorisations", icon: "📄" },
    { value: "Pénalité de retard", label: "Pénalité de retard", icon: "⚠️" },
    { value: "Autre", label: "Autre", icon: "📦" },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation avec Zod
    const result = fraisSchema.safeParse(formData);
    if (!result.success) {
      const firstError = result.error.errors[0];
      toast({
        title: "Données invalides",
        description: firstError.message,
        variant: "destructive",
      });
      return;
    }

    // Validation spécifique Sous-traitant : contrat OBLIGATOIRE
    if (formData.type_frais === 'Sous-traitant') {
      if (!selectedSTId) {
        toast({
          title: "Sous-traitant requis",
          description: "Veuillez sélectionner un sous-traitant pour ce type de coût",
          variant: "destructive",
        });
        return;
      }
      if (!selectedContratId) {
        toast({
          title: "Contrat requis",
          description: "Veuillez sélectionner ou créer un contrat pour ce sous-traitant. Le contrat permet de suivre les paiements.",
          variant: "destructive",
        });
        return;
      }
    }

    // Protection double-clic
    if (loading) return;
    setLoading(true);

    try {
      // Récupérer entreprise_id
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data: entreprise } = await supabase
        .from('entreprises')
        .select('id')
        .eq('proprietaire_user_id', user.id)
        .single();

      if (!entreprise) throw new Error("Entreprise introuvable");

      // Pour les frais sous-traitant sans description, auto-remplir avec les infos du ST
      let descriptionToSave = formData.description;
      if (formData.type_frais === 'Sous-traitant' && !formData.description.trim() && selectedSTId) {
        const st = sousTraitants.find(s => s.id === selectedSTId);
        if (st) {
          descriptionToSave = st.nom;
          if (st.specialite) {
            descriptionToSave += ` (${getSpecialiteLabel(st.specialite)})`;
          }
        }
      }

      // Préparer les données avec contrat_st_id si applicable
      const fraisData: Record<string, any> = {
        chantier_id: chantierId,
        type_frais: formData.type_frais,
        montant_ht: formData.montant_ht, // Prioritaire pour les calculs de rentabilité
        montant_total: formData.montant_ht, // Rétrocompatibilité (reste HT)
        tva_pct: formData.tva_pct, // TVA% → trigger calcule montant_ttc
        date_frais: formData.date_frais,
        description: descriptionToSave,
      };

      // Insertion du frais
      let newId: string | null = null;

      if (formData.type_frais === 'Sous-traitant' && selectedContratId) {
        // Pour les frais ST : INSERT direct pour inclure contrat_st_id
        // (la RPC insert_extraction_service n'inclut pas ce champ)
        const { data: insertedFrais, error: insertError } = await supabase
          .from('frais_chantier')
          .insert({
            entreprise_id: entreprise.id,
            chantier_id: chantierId,
            type_frais: formData.type_frais,
            montant_ht: formData.montant_ht,
            montant_total: formData.montant_ht, // Rétrocompatibilité (reste HT)
            tva_pct: formData.tva_pct, // TVA% → trigger calcule montant_ttc
            description: descriptionToSave,
            date_frais: formData.date_frais,
            contrat_st_id: selectedContratId,
          })
          .select('id')
          .single();

        if (insertError) throw insertError;
        newId = insertedFrais?.id || null;
      } else {
        // Pour les autres types : utiliser secureInsert (RPC)
        newId = await secureInsert({
          table: 'frais_chantier',
          data: fraisData,
          entrepriseId: entreprise.id
        });
      }

      if (!newId) {
        throw new Error("Échec de l'insertion");
      }

      setOpen(false);
      toast({ title: "Frais enregistré" });
      setFormData({
        type_frais: "",
        montant_ht: 0,
        tva_pct: 20,
        description: "",
        date_frais: new Date().toISOString().split('T')[0],
      });
      // Reset les sélections ST
      setSelectedSTId(null);
      setSelectedContratId(null);
      setTypeFacture('facture');
      // Recharger les contrats ST si on a ajouté un frais lié
      if (selectedContratId) {
        refetchContratsST();
      }
      onUpdate();
    } catch (error: any) {
      console.error('[ExpensesManager] Insert error:', error);

      toast({
        title: "Impossible d'enregistrer le coût",
        description: "Vérifiez les informations saisies et réessayez. Si le problème persiste, rechargez la page.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("frais_chantier")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Le frais a été supprimé",
      });
      onUpdate();
    } catch (error: any) {
      toast({
        title: "Suppression impossible",
        description: "Le coût n'a pas pu être supprimé. Réessayez ou rechargez la page.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) return;
    setIsDeletingFrais(true);
    try {
      await handleDelete(deleteTarget);
      setDeleteTarget(null);
    } finally {
      setIsDeletingFrais(false);
    }
  };

  const handleEdit = (fraisItem: any) => {
    setEditData({
      id: fraisItem.id,
      type_frais: fraisItem.type_frais || "",
      montant_ht: fraisItem.montant_ht ?? fraisItem.montant_total ?? 0, // Priorité à montant_ht
      tva_pct: fraisItem.tva_pct ?? 20, // TVA% existante ou 20% par défaut
      description: fraisItem.description || "",
      date_frais: fraisItem.date_frais || new Date().toISOString().split('T')[0],
      contrat_st_id: fraisItem.contrat_st_id || null, // Conserver le lien contrat existant
    });

    // Si c'est un frais Sous-traitant avec un contrat lié, pré-remplir les sélections
    if (fraisItem.type_frais === 'Sous-traitant' && fraisItem.contrat_st_id) {
      const contrat = contratsSTChantier.find(c => c.contrat_id === fraisItem.contrat_st_id);
      if (contrat) {
        setEditSelectedSTId(contrat.sous_traitant_id);
        setEditSelectedContratId(contrat.contrat_id);
      }
    } else {
      setEditSelectedSTId(null);
      setEditSelectedContratId(null);
    }
    setEditTypeFacture('facture');

    setEditDialogOpen(true);
  };

  const handleUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editData) return;

    // Validation avec Zod
    const result = fraisSchema.safeParse(editData);
    if (!result.success) {
      const firstError = result.error.errors[0];
      toast({
        title: "Données invalides",
        description: firstError.message,
        variant: "destructive",
      });
      return;
    }

    // Validation spécifique Sous-traitant : contrat OBLIGATOIRE
    if (editData.type_frais === 'Sous-traitant') {
      if (!editSelectedSTId) {
        toast({
          title: "Sous-traitant requis",
          description: "Veuillez sélectionner un sous-traitant pour ce type de coût",
          variant: "destructive",
        });
        return;
      }
      if (!editSelectedContratId) {
        toast({
          title: "Contrat requis",
          description: "Veuillez sélectionner ou créer un contrat pour ce sous-traitant. Le contrat permet de suivre les paiements.",
          variant: "destructive",
        });
        return;
      }
    }

    // Protection double-clic
    if (editLoading) return;
    setEditLoading(true);
    try {
      // Préparer les données de mise à jour
      const updateData: Record<string, any> = {
        type_frais: editData.type_frais,
        montant_ht: editData.montant_ht, // Prioritaire pour les calculs de rentabilité
        montant_total: editData.montant_ht, // Rétrocompatibilité (reste HT)
        tva_pct: editData.tva_pct, // TVA% → trigger calcule montant_ttc
        description: editData.description,
        date_frais: editData.date_frais,
      };

      // Ajouter ou retirer le contrat_st_id selon le type
      if (editData.type_frais === 'Sous-traitant' && editSelectedContratId) {
        updateData.contrat_st_id = editSelectedContratId;
      } else {
        // Si on change vers un autre type, on retire le lien contrat
        updateData.contrat_st_id = null;
      }

      const { error } = await supabase
        .from('frais_chantier')
        .update(updateData)
        .eq('id', editData.id);

      if (error) throw error;

      toast({ title: "✅ Coût annexe mis à jour" });
      setEditDialogOpen(false);
      setEditData(null);
      // Reset les sélections ST éditées
      setEditSelectedSTId(null);
      setEditSelectedContratId(null);
      setEditTypeFacture('facture');
      // Recharger les contrats ST si on a modifié un lien
      refetchContratsST();
      onUpdate();
    } catch (error: any) {
      toast({
        title: "Mise à jour impossible",
        description: "Les modifications n'ont pas pu être enregistrées. Réessayez.",
        variant: "destructive",
      });
    } finally {
      setEditLoading(false);
    }
  };

  // Utiliser montant_ht en priorité pour les calculs de rentabilité
  const totalFrais = frais.reduce((sum, f) => sum + Number(f.montant_ht ?? f.montant_total), 0);

  return (
    <>
      {/* Récapitulatif financier */}
      {frais.length > 0 && (
        <Card className="mb-4 bg-gradient-to-br from-orange-500/5 to-orange-500/10 border-orange-500/20">
          <CardHeader>
            <CardTitle className="text-lg">📦 Récapitulatif Coûts Annexes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-medium">Nombre de dépenses</p>
                <p className="text-3xl font-black text-foreground">{frais.length}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground font-medium">Total coûts annexes</p>
                <p className="text-3xl font-black text-orange-600">{totalFrais.toFixed(2)} €</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Coûts annexes</CardTitle>
            <CardDescription>
              Total : {totalFrais.toLocaleString()} €
            </CardDescription>
          </div>
          <Dialog open={open} onOpenChange={(isOpen) => {
            // Bloquer la fermeture si l'OCR a des données (multi-scan ou mono)
            if (!isOpen && ocrHasDataRef.current) {
              return;
            }
            setOpen(isOpen);
            if (!isOpen) {
              // Reset l'état de guidage quand on ferme la modal
              setTypeClicked(false);
            }
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Nouvelle dépense
              </Button>
            </DialogTrigger>
            <DialogContent variant="industrial" className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Ajouter un coût annexe</DialogTitle>
                <DialogDescription>
                  Importez un justificatif ou saisissez manuellement
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Type de coût - Partagé entre les deux onglets */}
                <div className="space-y-2">
                  <Label htmlFor="type_frais">Type de coût <span className="text-destructive">*</span></Label>
                  <Select
                    value={formData.type_frais}
                    onValueChange={(value) => setFormData({ ...formData, type_frais: value })}
                    onOpenChange={(isOpen) => { if (isOpen) setTypeClicked(true); }}
                    required
                  >
                    <SelectTrigger className={cn(
                      !formData.type_frais ? "text-muted-foreground" : "",
                      shouldHighlightType && "animate-subtle-glow"
                    )}>
                      <SelectValue placeholder="Sélectionner un type de coût" />
                    </SelectTrigger>
                    <SelectContent>
                      {expenseCategories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.icon} {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Permet de voir où part votre argent dans les graphiques de rentabilité.</p>
                </div>

                {/* Section Sous-traitant conditionnelle */}
                {formData.type_frais === 'Sous-traitant' && entrepriseId && (
                  <>
                    <p className="text-xs text-muted-foreground">Lier à un contrat permet de suivre les paiements échelonnés de ce sous-traitant.</p>
                    <SousTraitantSection
                      entrepriseId={entrepriseId}
                      chantierId={chantierId}
                      selectedSTId={selectedSTId}
                      onSTChange={setSelectedSTId}
                      selectedContratId={selectedContratId}
                      onContratChange={setSelectedContratId}
                      typeFacture={typeFacture}
                      onTypeFactureChange={setTypeFacture}
                      montantSaisi={formData.montant_ht}
                    />
                  </>
                )}

                {/* Onglets Import OCR / Saisie manuelle */}
                <Tabs defaultValue="ocr">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="ocr">Scan IA</TabsTrigger>
                    <TabsTrigger value="manual">Saisie manuelle</TabsTrigger>
                  </TabsList>

                  <TabsContent value="ocr">
                    <div>
                      <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                        <span className="bg-primary/10 text-primary px-2 py-0.5 rounded text-xs">IA</span>
                        Import automatique
                      </h3>
                      <ClaudeVisionImport
                        documentType="frais"
                        chantierId={chantierId}
                        onSuccess={() => {
                          ocrHasDataRef.current = false;
                          setOpen(false);
                          onUpdate();
                        }}
                        onCancel={() => {
                          ocrHasDataRef.current = false;
                          setOpen(false);
                        }}
                        onDataExtracted={() => {
                          ocrHasDataRef.current = true;
                        }}
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="manual">
                    <form onSubmit={handleSubmit}>
                      <div className="grid gap-4">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="montant_ht">Montant HT (€)</Label>
                            <Input
                              id="montant_ht"
                              type="number"
                              step="0.01"
                              value={formData.montant_ht}
                              onChange={(e) => setFormData({ ...formData, montant_ht: parseFloat(e.target.value) || 0 })}
                              required
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="tva_pct">TVA %</Label>
                            <Select
                              value={String(formData.tva_pct)}
                              onValueChange={(value) => setFormData({ ...formData, tva_pct: parseFloat(value) })}
                            >
                              <SelectTrigger id="tva_pct">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">0%</SelectItem>
                                <SelectItem value="5.5">5,5%</SelectItem>
                                <SelectItem value="10">10%</SelectItem>
                                <SelectItem value="20">20%</SelectItem>
                              </SelectContent>
                            </Select>
                            {formData.montant_ht > 0 && (
                              <p className="text-xs text-muted-foreground">
                                TTC : {(formData.montant_ht * (1 + formData.tva_pct / 100)).toFixed(2)} €
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="date_frais">Date</Label>
                            <Input
                              id="date_frais"
                              type="date"
                              value={formData.date_frais}
                              onChange={(e) => setFormData({ ...formData, date_frais: e.target.value })}
                              required
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="description">Description</Label>
                          <Textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Détails des frais..."
                          />
                        </div>

                        <Button
                          type="submit"
                          disabled={loading || (formData.type_frais === 'Sous-traitant' && loadingContratsST)}
                          className="bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]"
                        >
                          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          {formData.type_frais === 'Sous-traitant' && loadingContratsST ? 'Chargement contrats...' : 'Ajouter'}
                        </Button>
                      </div>
                    </form>
                  </TabsContent>
                </Tabs>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>

      <CardContent>
        {frais.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {frais.map((fraisItem) => {
                const category = expenseCategories.find(c => c.value === fraisItem.type_frais);
                const icon = category?.icon || "📦";

                // Pour les frais ST sans description, afficher les infos du sous-traitant
                let descriptionDisplay = fraisItem.description || "-";
                if (fraisItem.type_frais === 'Sous-traitant' && !fraisItem.description && fraisItem.contrat_st_id) {
                  const contrat = contratsSTChantier.find(c => c.contrat_id === fraisItem.contrat_st_id);
                  if (contrat) {
                    descriptionDisplay = contrat.sous_traitant_nom;
                    if (contrat.sous_traitant_specialite) {
                      descriptionDisplay += ` (${getSpecialiteLabel(contrat.sous_traitant_specialite)})`;
                    }
                  }
                }

                return (
                  <TableRow key={fraisItem.id}>
                    <TableCell className="font-medium">
                      {icon} {fraisItem.type_frais}
                    </TableCell>
                    <TableCell>{descriptionDisplay}</TableCell>
                    <TableCell>{new Date(fraisItem.date_frais).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right font-mono">
                      {Number(fraisItem.montant_ht ?? fraisItem.montant_total).toLocaleString()} €
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(fraisItem)}
                          aria-label="Modifier"
                          title="Modifier"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(fraisItem.id)}
                          aria-label="Supprimer"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <p className="text-muted-foreground text-center py-8">
            Aucun coût annexe pour le moment. Cliquez sur "Nouvelle dépense" pour commencer.
          </p>
        )}
      </CardContent>
    </Card>

      {/* Récapitulatif des contrats sous-traitance (après coûts annexes) */}
      {contratsSTChantier && contratsSTChantier.length > 0 && (
        <ContratsSTRecap
          contrats={contratsSTChantier}
          facturesParContrat={facturesParContrat}
          onDeleteContrat={deleteContratST}
          onAddFacture={handleAddFactureFromContrat}
        />
      )}

      {/* Dialog de confirmation de suppression */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirmed}
        variant="delete"
        isLoading={isDeletingFrais}
      />

      {/* Dialog d'édition */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => {
        setEditDialogOpen(open);
        if (!open) {
          // Reset les sélections ST quand on ferme
          setEditSelectedSTId(null);
          setEditSelectedContratId(null);
          setEditTypeFacture('facture');
        }
      }}>
        <DialogContent variant="industrial" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le coût annexe</DialogTitle>
            <DialogDescription>Corriger les données du coût annexe</DialogDescription>
          </DialogHeader>
          {editData && (
            <form onSubmit={handleUpdateSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-type_frais">Type de coût</Label>
                <Select
                  value={editData.type_frais}
                  onValueChange={(value) => {
                    setEditData({ ...editData, type_frais: value });
                    // Reset les sélections ST si on change de type
                    if (value !== 'Sous-traitant') {
                      setEditSelectedSTId(null);
                      setEditSelectedContratId(null);
                    }
                  }}
                >
                  <SelectTrigger id="edit-type_frais">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseCategories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Section Sous-traitant conditionnelle en mode édition */}
              {editData.type_frais === 'Sous-traitant' && entrepriseId && (
                <SousTraitantSection
                  entrepriseId={entrepriseId}
                  chantierId={chantierId}
                  selectedSTId={editSelectedSTId}
                  onSTChange={setEditSelectedSTId}
                  selectedContratId={editSelectedContratId}
                  onContratChange={setEditSelectedContratId}
                  typeFacture={editTypeFacture}
                  onTypeFactureChange={setEditTypeFacture}
                  montantSaisi={editData.montant_ht}
                />
              )}

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-montant">Montant HT (€)</Label>
                  <Input
                    id="edit-montant"
                    type="number"
                    step="0.01"
                    value={editData.montant_ht}
                    onChange={(e) => setEditData({ ...editData, montant_ht: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-tva">TVA %</Label>
                  <Select
                    value={String(editData.tva_pct)}
                    onValueChange={(value) => setEditData({ ...editData, tva_pct: parseFloat(value) })}
                  >
                    <SelectTrigger id="edit-tva">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="5.5">5,5%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="20">20%</SelectItem>
                    </SelectContent>
                  </Select>
                  {editData.montant_ht > 0 && (
                    <p className="text-xs text-muted-foreground">
                      TTC : {(editData.montant_ht * (1 + editData.tva_pct / 100)).toFixed(2)} €
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-date">Date</Label>
                  <Input
                    id="edit-date"
                    type="date"
                    value={editData.date_frais}
                    onChange={(e) => setEditData({ ...editData, date_frais: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  placeholder="Détails des frais..."
                />
              </div>

              <DialogFooter className="flex justify-between sm:justify-between">
                <Button
                  type="button"
                  variant="destructive"
                  className="bg-[rgba(230,57,70,0.1)] border border-[rgba(230,57,70,0.25)] text-[#E63946] hover:bg-[rgba(230,57,70,0.15)]"
                  onClick={() => {
                    if (editData?.id) {
                      setDeleteTarget(editData.id);
                      setEditDialogOpen(false);
                    }
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </Button>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="text-white/30 bg-transparent hover:text-white/55 hover:bg-transparent">
                    Annuler
                  </Button>
                  <Button type="submit" disabled={editLoading || (editData?.type_frais === 'Sous-traitant' && loadingContratsST)} className="bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]">
                    {editLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {editData?.type_frais === 'Sous-traitant' && loadingContratsST ? 'Chargement contrats...' : 'Mettre à jour le frais'}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExpensesManager;
